import { useState } from "react";
import {
  Download,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  MoreHorizontal,
  Presentation,
  Trash2,
} from "lucide-react";
import type { Attachment } from "@/api/types";
import {
  cciAttachmentDownloadUrl,
  downloadAttachment,
  openAttachmentInNewTab,
  taskAttachmentDownloadUrl,
} from "@/api/taskAttachments";
import { useAttachmentBlob } from "./useAttachmentBlob";
import { AnchoredMenu } from "@/components/ui/anchored-menu";
import {
  formatAttachmentDate,
  formatFileSize,
  isPdf,
  isPreviewableImage,
} from "./constants";
import { AttachmentViewer } from "./AttachmentViewer";

export type AttachmentParentType = "Task" | "CClientContactInteraction";

/** Type-icon by mime (image/pdf/spreadsheet/presentation/generic). */
function typeIcon(mime: string) {
  const m = mime.toLowerCase();
  if (m.startsWith("image/")) return ImageIcon;
  if (m === "application/pdf") return FileText;
  if (m.includes("spreadsheet") || m.includes("excel")) return FileSpreadsheet;
  if (m.includes("presentation") || m.includes("powerpoint")) return Presentation;
  return FileText;
}

function downloadUrlFor(
  parentType: AttachmentParentType,
  parentId: string,
  attachmentId: string,
): string {
  return parentType === "Task"
    ? taskAttachmentDownloadUrl(parentId, attachmentId)
    : cciAttachmentDownloadUrl(parentId, attachmentId);
}

/** 48×48 thumbnail: real image (auth-blob) for previewable images, else a type icon. */
function Thumb({
  url,
  mime,
  filename,
  onClick,
}: {
  url: string;
  mime: string;
  filename: string;
  onClick?: () => void;
}) {
  const previewable = isPreviewableImage(mime);
  const Icon = typeIcon(mime);
  // G6 (B2-13): lazy — blob лише для видимих thumb-ів (IO-гейт у хуку),
  // скрол довантажує решту. Помилка → лишається іконка-плейсхолдер (як і до G6).
  const { ref, src } = useAttachmentBlob(url, { lazy: true, enabled: previewable });

  const box =
    "flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/40 text-muted-foreground";

  if (previewable) {
    return (
      <button
        type="button"
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={onClick}
        aria-label={`Переглянути ${filename}`}
        className={`${box} hover:text-foreground`}
      >
        {src ? (
          <img src={src} alt="" className="h-full w-full object-cover" />
        ) : (
          <ImageIcon className="h-5 w-5" aria-hidden />
        )}
      </button>
    );
  }
  return (
    <div className={box} aria-hidden>
      <Icon className="h-5 w-5" />
    </div>
  );
}

/** Compact icon-only action button (tooltip via title) — keeps the card narrow so
 * the filename never gets squeezed (text labels live in the viewer header). */
const ICON_BTN =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50";
const TEXT_BTN =
  "inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50";

/**
 * TASK-ATT polish (W12 §0) — compact saved-attachment list.
 *
 * Card: row1 = 48×48 thumbnail (image) / type-icon (pdf/office) + filename (1 line,
 * ellipsis + full-name tooltip); row2 = «size · author · date». Actions:
 *   - image  → [Переглянути] (AttachmentViewer modal) + [Завантажити] + [⋯]
 *   - pdf    → [Відкрити в новій вкладці] (browser viewer) + [Завантажити] + [⋯]
 *   - office → [Завантажити] + [⋯]
 * 0-dead: «Переглянути» only for previewable images. Delete lives in [⋯] + confirm,
 * shown only when `onRemove` is passed (caller passes it only when edit-allowed).
 */
export function AttachmentList({
  parentType,
  parentId,
  attachments,
  onRemove,
  emptyHint = "Вкладень ще немає.",
}: {
  parentType: AttachmentParentType;
  parentId: string;
  attachments: Attachment[];
  /** Shown only when editable/not-locked. Drops id from the bound list (= unlink). */
  onRemove?: (id: string) => void;
  emptyHint?: string;
}) {
  const [viewer, setViewer] = useState<{ url: string; filename: string } | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (attachments.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyHint}</p>;
  }

  const runAction = (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    void fn().finally(() => setBusyId(null));
  };

  return (
    <>
      <ul className="space-y-2">
        {attachments.map((a) => {
          const url = downloadUrlFor(parentType, parentId, a.id);
          const previewable = isPreviewableImage(a.mime);
          const pdf = isPdf(a.mime);
          const confirming = confirmId === a.id;
          return (
            <li
              key={a.id}
              className="flex items-center gap-3 rounded-md border bg-background px-3 py-2"
            >
              <Thumb
                url={url}
                mime={a.mime}
                filename={a.filename}
                onClick={
                  previewable
                    ? () => setViewer({ url, filename: a.filename })
                    : undefined
                }
              />

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium" title={a.filename}>
                  {a.filename}
                </div>
                <div className="truncate text-[12px] text-muted-foreground">
                  {formatFileSize(a.size)}
                  {a.uploadedBy ? ` · ${a.uploadedBy}` : ""}
                  {a.uploadedAt ? ` · ${formatAttachmentDate(a.uploadedAt)}` : ""}
                </div>
              </div>

              {confirming ? (
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="hidden text-[13px] text-muted-foreground sm:inline">
                    Видалити?
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      onRemove?.(a.id);
                      setConfirmId(null);
                    }}
                    className="inline-flex h-8 items-center rounded-md bg-destructive px-2.5 text-[13px] font-medium text-destructive-foreground hover:opacity-90"
                  >
                    Видалити
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmId(null)}
                    className={TEXT_BTN}
                  >
                    Скасувати
                  </button>
                </div>
              ) : (
                <div className="flex shrink-0 items-center gap-0.5">
                  {previewable && (
                    <button
                      type="button"
                      onClick={() => setViewer({ url, filename: a.filename })}
                      className={ICON_BTN}
                      title="Переглянути"
                      aria-label={`Переглянути ${a.filename}`}
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                  {pdf && (
                    <button
                      type="button"
                      disabled={busyId === a.id}
                      onClick={() => runAction(a.id, () => openAttachmentInNewTab(url))}
                      className={ICON_BTN}
                      title="Відкрити в новій вкладці"
                      aria-label={`Відкрити ${a.filename} в новій вкладці`}
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => runAction(a.id, () => downloadAttachment(url, a.filename))}
                    className={ICON_BTN}
                    title="Завантажити"
                    aria-label={`Завантажити ${a.filename}`}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                  </button>
                  {onRemove && (
                    <AnchoredMenu
                      align="end"
                      menuLabel={`Дії для ${a.filename}`}
                      className="w-44 p-1"
                      trigger={({ toggle }) => (
                        <button
                          type="button"
                          onClick={toggle}
                          aria-label={`Дії для ${a.filename}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <MoreHorizontal className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                    >
                      {({ close }) => (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            close();
                            setConfirmId(a.id);
                          }}
                          className="flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-sm text-destructive hover:bg-muted"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden /> Видалити
                        </button>
                      )}
                    </AnchoredMenu>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {viewer && (
        <AttachmentViewer
          downloadUrl={viewer.url}
          filename={viewer.filename}
          onClose={() => setViewer(null)}
        />
      )}
    </>
  );
}
